import React, { useState, useEffect } from 'react';
import { X, Upload, FileText, Image, Video } from 'lucide-react';

const PostForm = ({ post, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    content: '',
    media: [],
    isApproved: true
  });
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (post) {
      setFormData({
        content: post.content || '',
        media: post.media || [],
        isApproved: post.isApproved
      });
    }
  }, [post]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      media: [...prev.media, ...files]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index)
    }));
  };

  const getFileIcon = (file) => {
    if (file.type?.startsWith('image/')) {
      return <Image className="w-4 h-4 text-green-500" />;
    } else if (file.type?.startsWith('video/')) {
      return <Video className="w-4 h-4 text-blue-500" />;
    } else {
      return <FileText className="w-4 h-4 text-red-500" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    const newErrors = {};
    if (!formData.content.trim() && formData.media.length === 0) {
      newErrors.content = 'Either content or media is required';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    setUploadProgress(0);
    
    try {
      // Create FormData object
      const formDataToSend = new FormData();
      
      // Add content if it exists
      if (formData.content.trim()) {
        formDataToSend.append('content', formData.content);
      }
      
      // Add files if they exist
      if (formData.media && formData.media.length > 0) {
        formData.media.forEach((file) => {
          formDataToSend.append('media', file);
        });
      }
      
      // Add isApproved status
      formDataToSend.append('isApproved', formData.isApproved);
      
      // If editing, add post ID
      if (post && post._id) {
        formDataToSend.append('postId', post._id);
      }
      
      // Simulate upload progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Make API call
      const apiUrl = post 
        ? `${process.env.REACT_APP_API_BASE_URL}/posts/${post._id}`
        : `${process.env.REACT_APP_API_BASE_URL}/posts`;
      
      const response = await fetch(apiUrl, {
        method: post ? 'PUT' : 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      setUploadProgress(100);
      clearInterval(progressInterval);
      
      // Reset form on success
      setFormData({
        content: '',
        media: [],
        isApproved: true
      });
      
      // Call parent's onSubmit with the result
      if (onSubmit) {
        onSubmit(result);
      }
      
      // Show success message
      alert(post ? 'Post updated successfully!' : 'Post created successfully!');
      
    } catch (error) {
      console.error('Error submitting post:', error);
      setUploadProgress(0);
      alert(error.message || 'Failed to save post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {post ? 'Edit Post' : 'Create New Post'}
        </h3>
        {onCancel && (
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              rows={4}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.content ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="What's on your mind?"
            />
            {errors.content && (
              <p className="mt-1 text-sm text-red-600">{errors.content}</p>
            )}
          </div>

          {/* Media Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Media Files
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload files</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      multiple
                      accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                      className="sr-only"
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  Images, videos, PDFs, DOCs up to 100MB each
                </p>
              </div>
            </div>
          </div>

          {/* File List */}
          {formData.media.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Selected Files:</h4>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {formData.media.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-2">
                      {getFileIcon(file)}
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {file.name || file.originalName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approval Status (Admin only) */}
          <div className="flex items-center">
            <input
              id="isApproved"
              name="isApproved"
              type="checkbox"
              checked={formData.isApproved}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isApproved" className="ml-2 block text-sm text-gray-900">
              Approve post immediately
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : (post ? 'Update Post' : 'Create Post')}
            </button>
          </div>
        </form>
    </div>
  );
};

export default PostForm;
